import React, { FC, useState } from 'react';

import BusinessIcon from '@mui/icons-material/Business';
import CheckIcon from '@mui/icons-material/Check';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import Box from '@mui/material/Box';
import ButtonBase from '@mui/material/ButtonBase';
import CircularProgress from '@mui/material/CircularProgress';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';

import useCurrentUser from 'hooks/useCurrentUser';
import useSwitchProject from 'hooks/useSwitchProject';
import Error from 'pages/Error/ErrorAlert';
import { useGetIdentityProjectsQuery } from 'services/api/user.api';

import { StyledNavItemButton } from './Sidebar.styles';

const AZURE = '#3B82F6';
const BRAND_LIGHT = '#E0EDF6';

interface SwitcherProjectOption {
  id: number | null;
  name: string;
}

const ProjectIdLabel: FC<{ project: SwitcherProjectOption }> = ({ project }) => {
  if (project.id === null) return null;
  return (
    <Typography sx={{ fontSize: '11px', fontWeight: 500, color: '#6B7280', flexShrink: 0 }}>
      {`#${project.id}`}
    </Typography>
  );
};

interface Props {
  isCollapsed?: boolean;
}

const SidebarProjectSwitcher: FC<Props> = ({ isCollapsed = false }) => {
  const currentUser = useCurrentUser();
  const { data: identityProjectsData, isLoading: isLoadingProjects } = useGetIdentityProjectsQuery();
  const identityProjects: SwitcherProjectOption[] = (identityProjectsData?.projects ?? []).map((project) => ({
    id: project.projectId,
    name:
      project.projectName ?? (project.projectId !== null ? `Proyecto #${project.projectId}` : 'Cuenta administradora'),
  }));
  const selectedProjectId = currentUser?.projectId ?? null;
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const { switchToProject, isSwitching } = useSwitchProject();
  const [errorMessage, setErrorMessage] = useState('');

  if (isLoadingProjects || identityProjects.length < 2) return null;

  const selectedProject = identityProjects.find((project) => project.id === selectedProjectId) ?? identityProjects[0];
  const open = Boolean(anchorEl);

  const handleSelect = (project: SwitcherProjectOption): void => {
    setAnchorEl(null);
    if (project.id === selectedProject.id || isSwitching) return;

    switchToProject(project.id).catch(() => {
      setErrorMessage('No se pudo cambiar de inmobiliaria. Intenta nuevamente.');
    });
  };

  const expandedTrigger = (
    <ButtonBase
      aria-haspopup="menu"
      aria-expanded={open}
      aria-label="Cambiar de Inmobiliaria"
      onClick={(event) => setAnchorEl(event.currentTarget)}
      sx={{
        width: '100%',
        display: 'flex',
        alignItems: 'center',
        gap: '10px',
        p: '10px 12px',
        textAlign: 'left',
        borderRadius: '10px',
        backgroundColor: open ? '#FFFFFF' : '#F9FAFB',
        border: open ? `1.5px solid ${AZURE}` : '1px solid #E5E7EB',
        transition: 'background-color 120ms ease, border-color 120ms ease, box-shadow 120ms ease',
        '&:hover': { backgroundColor: '#FFFFFF' },
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px', flex: 1, minWidth: 0 }}>
        <Typography noWrap sx={{ fontSize: '13.5px', fontWeight: 600, color: '#111827', lineHeight: 1.3 }}>
          {selectedProject.name}
        </Typography>
        <ProjectIdLabel project={selectedProject} />
      </Box>
      {isSwitching ? (
        <CircularProgress size={14} sx={{ flexShrink: 0 }} />
      ) : (
        <ExpandMoreIcon
          sx={{
            width: '16px',
            height: '16px',
            color: 'text.secondary',
            flexShrink: 0,
            transform: open ? 'rotate(180deg)' : 'none',
            transition: 'transform 120ms ease',
          }}
        />
      )}
    </ButtonBase>
  );

  const collapsedTrigger = (
    <Tooltip title="Cambiar de Inmobiliaria" placement="right" arrow>
      <StyledNavItemButton
        component={'button' as React.ElementType}
        active={open}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label="Cambiar de Inmobiliaria"
        onClick={(event: React.MouseEvent<HTMLElement>) => setAnchorEl(event.currentTarget)}
        sx={{ width: '100%', justifyContent: 'center', px: 1 }}
      >
        <BusinessIcon className="sidebar-icon" style={{ width: '20px', height: '20px' }} />
      </StyledNavItemButton>
    </Tooltip>
  );

  return (
    <Box sx={{ pt: 2 }}>
      {isCollapsed ? collapsedTrigger : expandedTrigger}

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={
          isCollapsed ? { vertical: 'top', horizontal: 'right' } : { vertical: 'bottom', horizontal: 'left' }
        }
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        marginThreshold={0}
        slotProps={{
          paper: {
            sx: {
              mt: isCollapsed ? 0 : 1,
              ml: isCollapsed ? 1 : 0,
              width: isCollapsed ? 240 : anchorEl?.offsetWidth,
              borderRadius: '12px',
              border: '1px solid #E5E7EB',
              boxShadow: '0 8px 24px rgba(15, 23, 42, 0.14)',
            },
          },
        }}
        MenuListProps={{ sx: { p: '6px' } }}
      >
        {identityProjects.map((project) => {
          const isSelected = project.id === selectedProject.id;
          return (
            <MenuItem
              key={project.id ?? 'admin'}
              onClick={() => handleSelect(project)}
              disabled={isSwitching}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
                p: '8px 10px',
                mb: '2px',
                borderRadius: '8px',
                backgroundColor: isSelected ? BRAND_LIGHT : 'transparent',
                '&:hover': { backgroundColor: isSelected ? BRAND_LIGHT : '#F9FAFB' },
                '&:last-of-type': { mb: 0 },
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px', flex: 1, minWidth: 0 }}>
                <Typography
                  noWrap
                  sx={{
                    fontSize: '13px',
                    fontWeight: isSelected ? 600 : 500,
                    color: isSelected ? '#111827' : '#4B5563',
                  }}
                >
                  {project.name}
                </Typography>
                {project.id !== null && (
                  <Typography sx={{ fontSize: '10.5px', fontWeight: 500, color: '#9CA3AF', flexShrink: 0 }}>
                    {`#${project.id}`}
                  </Typography>
                )}
              </Box>
              {isSelected && <CheckIcon sx={{ fontSize: 16, color: AZURE, flexShrink: 0 }} />}
            </MenuItem>
          );
        })}
      </Menu>

      <Error message={errorMessage} isOpen={!!errorMessage} />
    </Box>
  );
};

export default SidebarProjectSwitcher;
